import type { ClassificationType } from "@/types/entity";
import type {
  TargetareAmbiguitateServiciu,
  TargetareDimensiune,
} from "@/types/targetare";
import { ETICHETE_REGULA } from "@/utils/targetareConfig";
import {
  raportImplicit,
  type DefinitieRaport,
  type FiltruRapidDef,
} from "@/utils/targetareRaport";
import type { ColoanaTabel } from "@/utils/targetareTabel";

export const DIMENSIUNI: TargetareDimensiune[] = [
  "micro",
  "mica",
  "mijlocie",
  "mare",
];

export function rezultatRegula(
  tip: string | null,
  dim: TargetareDimensiune
): ClassificationType {
  const mijlocieSauMare = dim === "mijlocie" || dim === "mare";
  switch (tip) {
    case "2":
    case "3":
    case "4":
      return "ESSENTIAL";
    case "5":
      return dim === "mare" ? "ESSENTIAL" : "IMPORTANT";
    case "6":
      return mijlocieSauMare ? "ESSENTIAL" : "IMPORTANT";
    case "7":
      return mijlocieSauMare ? "ESSENTIAL" : "OUT_OF_SCOPE";
    case "8":
    case "9":
      return dim === "mare"
        ? "ESSENTIAL"
        : dim === "mijlocie"
          ? "IMPORTANT"
          : "OUT_OF_SCOPE";
    case "11":
    case "12":
      return mijlocieSauMare ? "IMPORTANT" : "OUT_OF_SCOPE";
    default:
      return "OUT_OF_SCOPE";
  }
}

export type RandRegula = TargetareAmbiguitateServiciu & {
  cheie: string;
  type_text: string;
  sector_text: string;
  anexa_text: string;
  regula_text: string;
  regula_descriere: string;
  rezultat_micro: ClassificationType;
  rezultat_mica: ClassificationType;
  rezultat_mijlocie: ClassificationType;
  rezultat_mare: ClassificationType;
};

export function randuriReguli(
  servicii: TargetareAmbiguitateServiciu[]
): RandRegula[] {
  return servicii.map((s) => ({
    ...s,
    cheie: s.entity_id,
    type_text: s.type ?? "(fără)",
    sector_text: s.sector ?? "(fără sector)",
    anexa_text: s.anexa ?? "",
    regula_text: s.regula_tip ?? "",
    regula_descriere: s.regula_tip
      ? (ETICHETE_REGULA[s.regula_tip] ?? s.regula_tip)
      : "(fără regulă: serviciul nu încadrează)",
    rezultat_micro: rezultatRegula(s.regula_tip, "micro"),
    rezultat_mica: rezultatRegula(s.regula_tip, "mica"),
    rezultat_mijlocie: rezultatRegula(s.regula_tip, "mijlocie"),
    rezultat_mare: rezultatRegula(s.regula_tip, "mare"),
  }));
}

export const ETICHETE_REZULTAT: Record<string, string> = {
  ESSENTIAL: "esențială",
  IMPORTANT: "importantă",
  OUT_OF_SCOPE: "în afara scopului",
};

export const COLOANE_REGULI: ColoanaTabel<RandRegula>[] = [
  {
    id: "sector_text",
    eticheta: "Sector",
    tip: "lista",
    latime: 150,
    implicit: true,
  },
  {
    id: "name",
    eticheta: "Serviciu NIS2",
    tip: "text",
    latime: 280,
    implicit: true,
  },
  { id: "entity_id", eticheta: "Id", tip: "text", latime: 80, implicit: true },
  {
    id: "anexa_text",
    eticheta: "Anexă",
    tip: "lista",
    latime: 70,
    implicit: true,
  },
  {
    id: "regula_text",
    eticheta: "Regula",
    tip: "lista",
    latime: 250,
    implicit: true,
    etichete: ETICHETE_REGULA,
  },
  {
    id: "orice_dimensiune",
    eticheta: "Orice dimensiune",
    tip: "bool",
    latime: 110,
    implicit: true,
  },
  {
    id: "rezultat_micro",
    eticheta: "Micro",
    tip: "lista",
    latime: 110,
    implicit: true,
    etichete: ETICHETE_REZULTAT,
  },
  {
    id: "rezultat_mica",
    eticheta: "Mică",
    tip: "lista",
    latime: 110,
    implicit: true,
    etichete: ETICHETE_REZULTAT,
  },
  {
    id: "rezultat_mijlocie",
    eticheta: "Mijlocie",
    tip: "lista",
    latime: 110,
    implicit: true,
    etichete: ETICHETE_REZULTAT,
  },
  {
    id: "rezultat_mare",
    eticheta: "Mare",
    tip: "lista",
    latime: 110,
    implicit: true,
    etichete: ETICHETE_REZULTAT,
  },
  {
    id: "firme_principal",
    eticheta: "Firme prin CAEN",
    tip: "numar",
    latime: 100,
    implicit: false,
  },
  {
    id: "firme_lista",
    eticheta: "Firme de pe liste",
    tip: "numar",
    latime: 100,
    implicit: false,
  },
  {
    id: "regula_descriere",
    eticheta: "Descrierea regulii",
    tip: "text",
    latime: 320,
    implicit: false,
  },
];

export const FILTRE_RAPIDE_REGULI: FiltruRapidDef[] = [
  { coloana: "sector_text", eticheta: "Sector" },
  { coloana: "anexa_text", eticheta: "Anexă" },
  { coloana: "regula_text", eticheta: "Regula", etichete: ETICHETE_REGULA },
  {
    coloana: "orice_dimensiune",
    eticheta: "Orice dimensiune",
    optiuni: ["da", "nu"],
  },
];

const IMPLICITE = COLOANE_REGULI.filter((c) => c.implicit).map((c) => c.id);

export const definitieRaportReguli: DefinitieRaport<RandRegula> = {
  cheie: "targetare.rapoarte.reguli",
  coloane: COLOANE_REGULI,
  rapoarteImplicite: [
    raportImplicit("Regulile pe servicii", IMPLICITE, {
      sortare: [{ id: "sector_text", desc: false }],
      randuriPePagina: 100,
    }),
    raportImplicit("Servicii la orice dimensiune", IMPLICITE, {
      filtre: [
        {
          id: "f-od",
          coloana: "orice_dimensiune",
          operator: "egal",
          valoare: "da",
          activ: true,
        },
      ],
    }),
  ],
  cheieRand: (r) => r.cheie,
  filtreRapide: FILTRE_RAPIDE_REGULI,
  numeFisier: "reguli-incadrare",
  mesajGol: "Serviciile nu au putut fi încărcate.",
};
